import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/ui/toast";
import { adminApi } from "../services/adminApi";
import type {
  AdminUser,
  UsageLimit,
  UsageLimitPayload,
  UsageEvent,
} from "../types/admin";

interface UsageLimitState {
  data: UsageLimit | null;
  isLoading: boolean;
  error: string | null;
}

interface UsageEventsState {
  data: UsageEvent[];
  isLoading: boolean;
  error: string | null;
}

export interface UseAdminUsersResult {
  users: AdminUser[];
  filteredUsers: AdminUser[];
  selectedUser: AdminUser | null;
  search: string;
  isLoading: boolean;
  error: string | null;
  usageLimit: UsageLimitState;
  usageEvents: UsageEventsState;
  selectUser: (userId: string | null) => void;
  setSearch: (value: string) => void;
  refreshUsers: () => Promise<void>;
  createUser: (payload: {
    email: string;
    display_name?: string | null;
    verified?: boolean;
  }) => Promise<void>;
  setAdminStatus: (userId: string, isAdmin: boolean) => Promise<void>;
  updateUsageLimit: (userId: string, payload: UsageLimitPayload) => Promise<void>;
  refreshUsageEvents: (userId: string, startAt: number, endAt: number) => Promise<void>;
}

export function useAdminUsers(): UseAdminUsersResult {
  const { user } = useAuth();
  const { push } = useToast();
  const isAdmin = Boolean((user as any)?.is_admin);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageLimit, setUsageLimit] = useState<UsageLimitState>({
    data: null,
    isLoading: false,
    error: null,
  });
  const [usageEvents, setUsageEvents] = useState<UsageEventsState>({
    data: [],
    isLoading: false,
    error: null,
  });

  const selectedUser = useMemo(() => {
    return users.find((u) => u.id === selectedUserId) || null;
  }, [users, selectedUserId]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => {
      const name = user.display_name || "";
      return (
        user.email.toLowerCase().includes(term)
        || name.toLowerCase().includes(term)
      );
    });
  }, [users, search]);

  const refreshUsers = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    setError(null);
    try {
      const list = await adminApi.listUsers();
      setUsers(list);
      if (list.length && !selectedUserId) {
        setSelectedUserId(list[0].id);
      }
    } catch (err: any) {
      const message = err?.message || "Failed to load users";
      setError(message);
      push({
        title: "Admin",
        description: message,
        variant: "destructive",
        durationMs: 6000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, push, selectedUserId]);

  const loadUsageLimit = useCallback(async (userId: string | null) => {
    if (!userId || !isAdmin) return;
    setUsageLimit({ data: null, isLoading: true, error: null });
    try {
      const limit = await adminApi.getUsageLimit(userId);
      setUsageLimit({ data: limit, isLoading: false, error: null });
    } catch (err: any) {
      const message = err?.message || "Failed to load usage limit";
      setUsageLimit({ data: null, isLoading: false, error: message });
    }
  }, [isAdmin]);

  const refreshUsageEvents = useCallback(async (userId: string, startAt: number, endAt: number) => {
    if (!isAdmin) return;
    setUsageEvents({ data: [], isLoading: true, error: null });
    try {
      const events = await adminApi.listUsageEvents(userId, {
        start_at: startAt,
        end_at: endAt,
        limit: 1000,
      });
      setUsageEvents({ data: events, isLoading: false, error: null });
    } catch (err: any) {
      const message = err?.message || "Failed to load usage events";
      setUsageEvents({ data: [], isLoading: false, error: message });
    }
  }, [isAdmin]);

  const createUser = useCallback(async (payload: {
    email: string;
    display_name?: string | null;
    verified?: boolean;
  }) => {
    try {
      await adminApi.createUser(payload);
      await refreshUsers();
      push({
        title: "Admin",
        description: "User created",
      });
    } catch (err: any) {
      const message = err?.message || "Failed to create user";
      push({
        title: "Admin",
        description: message,
        variant: "destructive",
        durationMs: 6000,
      });
      throw err;
    }
  }, [push, refreshUsers]);

  const setAdminStatus = useCallback(async (userId: string, isAdminValue: boolean) => {
    try {
      await adminApi.setAdminStatus(userId, isAdminValue);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, is_admin: isAdminValue } : u
        )
      );
      push({
        title: "Admin",
        description: isAdminValue ? "User promoted to admin" : "User demoted",
      });
    } catch (err: any) {
      const message = err?.message || "Failed to update admin status";
      push({
        title: "Admin",
        description: message,
        variant: "destructive",
        durationMs: 6000,
      });
      throw err;
    }
  }, [push]);

  const updateUsageLimit = useCallback(async (userId: string, payload: UsageLimitPayload) => {
    try {
      const limit = await adminApi.setUsageLimit(userId, payload);
      setUsageLimit({ data: limit, isLoading: false, error: null });
      push({
        title: "Admin",
        description: "Usage limit updated",
      });
    } catch (err: any) {
      const message = err?.message || "Failed to update usage limit";
      setUsageLimit((prev) => ({ ...prev, error: message, isLoading: false }));
      push({
        title: "Admin",
        description: message,
        variant: "destructive",
        durationMs: 6000,
      });
      throw err;
    }
  }, [push]);

  useEffect(() => {
    if (isAdmin) {
      refreshUsers();
    }
  }, [isAdmin, refreshUsers]);

  useEffect(() => {
    loadUsageLimit(selectedUserId);
  }, [loadUsageLimit, selectedUserId]);

  const selectUser = useCallback((userId: string | null) => {
    setSelectedUserId(userId);
  }, []);

  return {
    users,
    filteredUsers,
    selectedUser,
    search,
    isLoading,
    error,
    usageLimit,
    usageEvents,
    selectUser,
    setSearch,
    refreshUsers,
    createUser,
    setAdminStatus,
    updateUsageLimit,
    refreshUsageEvents,
  };
}
